import {defineStore} from 'pinia';
import redirect_error_page from "@/util/redirect_error_page"
import {ISettings, INotification, ILicenseExpiration, IStudentProfile} from "@/consts/interfaces";
import { useVersionStore } from './version_store';
import apiRequest from '@/util/api-request';
import { AxiosRequestConfig } from 'axios';

interface AccountState {
    profile: IStudentProfile | null;
    is_gtag_config: boolean;
    license_expiration: Array<ILicenseExpiration>;
    user_id: number | null;
    settings: ISettings;
    notifications: Array<INotification>,
    already_read_notifications: {[id: string] : string};
    debug_souruby_enabled: boolean | null;
}

export const useAccountStore = defineStore('accountStore', {
    // state
    state: (): AccountState => {
        return {
            profile: null,
            is_gtag_config: false,
            license_expiration: [],
            user_id: null,
            settings: {
                souruby_enabled: null,
                is_debug: false,
                is_break_time: true,
                is_sound_enabled: true,
                is_tts_enabled: true,
                playback_rate: 1.0,
            },
            debug_souruby_enabled: null,
            notifications: [],
            already_read_notifications: {}
        }
    },
    // getters
    getters: {
        studentProfile: (state) => {
            return state.profile
        },
        schoolName: (state) => {
          return state.profile.profile.school.name
        },
        academicYear: (state) => {
            return state.profile.profile.classroom.academic_year
        },
        realAcademicYear: (state) => {
            return state.profile.profile.real_academic_year
        },
        licenseExpiration: (state) => {
            return state.license_expiration
        },
        isGtagConfig: (state) => {
            return state.is_gtag_config
        },
        userId: (state) => {
            return state.user_id
        },
        isSouruby: (state) => {
            return state.settings.souruby_enabled
        },
        isDebugSouruby: (state) => {
            return state.debug_souruby_enabled
        },
        isDebug: (state) => {
            return state.settings.is_debug
        },
        notificationList: (state) => {
            return state.notifications
        },
        alreadyReadNotification: (state) => {
            return state.already_read_notifications
        },
        unReadNotification: (state) => {
            return state.notifications.filter(item => !state.already_read_notifications[item.uuid]).length
        },
        importantUnReadNotice: (state) => {
            return state.notifications.filter(item => !state.already_read_notifications[item.uuid] && item.is_important)
        },
        isBreakTime : (state) =>{
            return state.settings.is_break_time
        },
        isSoundEnabled: (state) =>{
            return state.settings.is_sound_enabled
        },
        isTtsEnabled: (state) => {
            return state.settings.is_tts_enabled
        },
        playbackRate: (state) => {
            return state.settings.playback_rate
        }
    },
    // actions
    actions: {
        async fetchAccountProfile() {
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: '/student/api/account/profile',
                };
                const student_profile = await apiRequest(config);
                this.profile = student_profile.data
                versionStore.saveAppVersion(student_profile.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchLicenseExpiration() {
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: '/student/api/account/license_expiration',
                };
                const response = await apiRequest(config);
                this.license_expiration = response.data.license_expirations
                versionStore.saveAppVersion(response.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchListSettings() {
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: '/student/api/account/list_settings',
                };
                const response = await apiRequest(config);
                this.settings = response.data.settings
                versionStore.saveAppVersion(response.headers["x-app-version"])
                return response.data.settings
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchUpdateSettings(payload: any){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/account/update_settings',
                    data: payload,
                };
                const response = await apiRequest(config);
                versionStore.saveAppVersion(response.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchListNotifications(){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: '/student/api/notification/list_notification',
                };
                const response = await apiRequest(config);
                this.notifications = response.data.notifications
                versionStore.saveAppVersion(response.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchAlreadyReadNotifications(){
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'get',
                    url: '/student/api/notification/list_already_read',
                };
                const response = await apiRequest(config);
                let already_read_notifications: {[id: string] : string} = {}
                for(const uuid of response.data.already_read.notification_uuids){
                    already_read_notifications[uuid] = uuid
                }
                this.already_read_notifications = already_read_notifications
                versionStore.saveAppVersion(response.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        async fetchReadNotification(payload: {notification_uuid: string}){
            // payload contains
            // {
            //   "notification_uuid": "f9b13da3-37f8-4d91-a553-2a4e822305ef"
            // }
            const versionStore = useVersionStore()
            try {
                const config: AxiosRequestConfig = {
                    method: 'post',
                    url: '/student/api/notification/read',
                    data: payload,
                };
                const response = await apiRequest(config);
                versionStore.saveAppVersion(response.headers["x-app-version"])
            }
            catch (e) {
                redirect_error_page(e)
            }
        },
        saveUserId(value: number){
            this.user_id = value
        },
        saveIsGtagConfig(value: boolean) {
            this.is_gtag_config = value
        },
        saveDebugSouruby(value: boolean | null) {
            this.debug_souruby_enabled = value
        },
        saveRubySetting(value: boolean | null) {
            this.settings.souruby_enabled = value
        },
        saveBreakTimeSetting(value: boolean){
            this.settings.is_break_time = value
        },
        saveSoundEnabledSetting(value: boolean){
            this.settings.is_sound_enabled = value
        },
        saveTtsEnabledSettings(value: boolean) {
            this.settings.is_tts_enabled = value
        },
        savePlaybackRate(value: number) {
            this.settings.playback_rate = value
        }
    }
})